import {
  Badge,
  Box,
  Input,
  InputGroup,
  InputRightAddon,
  useColorMode,
} from '@chakra-ui/react';
import * as math from 'mathjs';
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LineSegment2, Point2 } from '../../../domain/geometry/geometric-types';
import { calculateSegmentLength } from '../../../domain/geometry/algorithms/util/line-segment';
import { getUnitScaleFromCalibration } from '../../../domain/sheet-calibration';
import { SheetScaleType } from '../../../gql/graphql';

type SheetTranslationFeedbackLineProps = {
  /** Start point (reference point) in PDF coordinates */
  startPoint: Point2;
  /** End point (cursor position) in PDF coordinates */
  endPoint: Point2;
  /** Function to convert PDF coordinates to DOM coordinates */
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2;
  /** Calibration data for unit conversion (if available) */
  calibration: SheetScaleType | null;
  /** Callback when user submits a custom length */
  onLengthSubmit?: (newLengthInPdfUnits: number) => void;
};

/**
 * Renders a dashed blue line from the reference point to the cursor position,
 * with an editable length label. Used during move/copy operations to show
 * the translation vector.
 */
export function SheetTranslationFeedbackLine({
  startPoint,
  endPoint,
  getPointInDomCoordinateSystem,
  calibration,
  onLengthSubmit,
}: SheetTranslationFeedbackLineProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { colorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';

  const segment: LineSegment2 = useMemo(
    () => [startPoint, endPoint],
    [startPoint, endPoint]
  );

  const unitScale = useMemo(
    () => (calibration ? getUnitScaleFromCalibration(calibration) : null),
    [calibration]
  );

  const lengthInPdfUnits = useMemo(
    () => calculateSegmentLength(segment),
    [segment]
  );

  const lengthInMeters = useMemo(
    () => (unitScale ? lengthInPdfUnits * unitScale : null),
    [lengthInPdfUnits, unitScale]
  );

  const segmentInDom = useMemo<LineSegment2>(() => {
    const p1 = getPointInDomCoordinateSystem(startPoint);
    const p2 = getPointInDomCoordinateSystem(endPoint);
    return [p1, p2];
  }, [getPointInDomCoordinateSystem, startPoint, endPoint]);

  // Calculate line properties
  const [x1, y1] = segmentInDom[0];
  const [x2, y2] = segmentInDom[1];
  const screenLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;

  const midpoint = useMemo(
    () => ({
      top: y1 - 0.5 * (y1 - y2),
      left: x1 - 0.5 * (x1 - x2),
    }),
    [x1, y1, x2, y2]
  );

  // Handle global keyboard events when not editing
  useEffect(() => {
    if (isEditing || !unitScale) return;

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Don't capture if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Check if key is a digit or decimal point
      if (/^[0-9.,]$/.test(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        setInputValue(e.key === ',' ? '.' : e.key);
      }
    };

    // Use capture phase to get events before the viewer
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isEditing, unitScale]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const parsedValue = parseFloat(inputValue);
      if (
        !isNaN(parsedValue) &&
        parsedValue > 0 &&
        onLengthSubmit &&
        unitScale
      ) {
        // Convert from meters back to PDF units
        const lengthInPdfUnits = parsedValue / unitScale;
        onLengthSubmit(lengthInPdfUnits);
      }
      setIsEditing(false);
      setInputValue('');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setInputValue('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(',', '.');
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      setInputValue(value);
    }
  };

  const handleInputBlur = useCallback(() => {
    setIsEditing(false);
    setInputValue('');
  }, []);

  // Always show the label, but only show the line if it's long enough
  const showLine = screenLength >= 3;

  const lineColor = 'var(--chakra-colors-blue-500)';

  return (
    <>
      {/* Dashed line from start to end */}
      {showLine && (
        <Box
          position="absolute"
          zIndex={1001}
          width="2px"
          pointerEvents="none"
          style={{
            top: y1,
            left: x1,
            height: `${screenLength}px`,
            transformOrigin: '1px 0',
            transform: `rotate(${angle - 90}deg)`,
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 6px, ${lineColor} 6px, ${lineColor} 12px)`,
          }}
        />
      )}
      {/* Length label at the midpoint */}
      <Box
        position="absolute"
        zIndex={1002}
        style={midpoint}
        pointerEvents={isEditing ? 'auto' : 'none'}
        transform="translate(-50%, -50%)"
      >
        {isEditing ? (
          <InputGroup size="xs">
            <Input
              ref={inputRef}
              width="45px"
              height="18px"
              minHeight="18px"
              textAlign="center"
              backgroundColor="white"
              borderColor="blue.400"
              borderRadius="sm"
              borderRightRadius={0}
              fontSize="2xs"
              px={1}
              py={0}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onBlur={handleInputBlur}
              autoFocus
            />
            <InputRightAddon
              height="18px"
              minHeight="18px"
              fontSize="2xs"
              px={1}
              borderRadius="sm"
              borderLeftRadius={0}
            >
              m
            </InputRightAddon>
          </InputGroup>
        ) : (
          <Badge
            textTransform="none"
            fontSize="2xs"
            px={1}
            py={0}
            borderRadius="sm"
            variant={!isDarkMode ? 'outline' : 'solid'}
            colorScheme="blue"
            backgroundColor={!isDarkMode ? 'white' : undefined}
          >
            {lengthInMeters !== null
              ? `${math.round(lengthInMeters, 2)} m`
              : `${math.round(lengthInPdfUnits, 2)} px`}
          </Badge>
        )}
      </Box>
    </>
  );
}
